import axios from 'axios';
import config, { getApiUrl } from './config.js';

// Prevents multiple in-flight 401 responses from each triggering a redirect.
let isRedirectingToLogin = false;

const axiosInstance = axios.create({
    baseURL: config.API_BASE_URL,
    timeout: config.API_TIMEOUT,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add request interceptor to add token (no expiry checking for now)
axiosInstance.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('access_token');

        // If we're sending FormData, let the browser/axios set the proper multipart boundary.
        // A default JSON content-type will cause FastAPI to return 422 (missing file).
        const isFormData =
            typeof FormData !== 'undefined' &&
            config.data &&
            (config.data instanceof FormData);
        if (isFormData) {
            // Axios will set `multipart/form-data; boundary=...` automatically
            // when Content-Type is not forced.
            try {
                delete config.headers?.['Content-Type'];
                delete config.headers?.common?.['Content-Type'];
            } catch {
                // ignore - safe best-effort
            }
        }
        
        // Only skip token for login endpoint specifically
        const isLoginEndpoint = config.url?.includes('/auth/token');
        
        if (token && !isLoginEndpoint) {
            config.headers.Authorization = `Bearer ${token}`;
            if (import.meta.env.VITE_DEBUG_MODE === 'true') {
                console.log("✅ Request headers with token for:", config.url);
                console.log("🔑 Authorization header:", config.headers.Authorization.substring(0, 30) + '...');
            }
        } else if (!isLoginEndpoint) {
            if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log("⚠️ No token found for request:", config.url);
        } else {
            if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log("🚫 Skipping token for login endpoint:", config.url);
        }
        return config;
    },
    (error) => {
        console.error('❌ Request interceptor error:', error);
        return Promise.reject(error);
    }
);

// Add response interceptor for handling token expiry responses
axiosInstance.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        const status = error.response?.status;
        const isExpected404 = status === 404;
        // 401 is handled silently below (redirect to login), so don't log as error.
        const isHandled401 = status === 401;
        if (!isExpected404 && !isHandled401 || import.meta.env.VITE_DEBUG_MODE === 'true') {
            console.error("❌ Axios response error:", {
                url: error.config?.url,
                method: error.config?.method,
                status,
                statusText: error.response?.statusText,
                data: error.response?.data,
            });
        }

        if (status === 401) {
            if (import.meta.env.VITE_DEBUG_MODE === 'true') {
                console.log('🔴 Received 401 - Token likely expired', { url: error.config?.url });
            }

            // Guard: if we're already mid-redirect, don't trigger again.
            if (isRedirectingToLogin || window.location.pathname === '/') {
                return Promise.reject(new Error('Session expired'));
            }
            isRedirectingToLogin = true;

            // If an impersonation session exists, restore the admin rather than logging out.
            try {
                const raw = localStorage.getItem('impersonation_session');
                if (raw) {
                    const session = JSON.parse(raw);
                    const orig = session?.originalSession;
                    if (orig?.access_token) {
                        localStorage.setItem('access_token', orig.access_token);
                        if (orig.role != null) localStorage.setItem('role', String(orig.role));
                        if (orig.isAdmin != null) localStorage.setItem('isAdmin', String(orig.isAdmin === true || orig.isAdmin === 'true'));
                        ['user_id', 'student_id', 'preceptor_id', 'admin_id', 'user_type', 'userType'].forEach(k => localStorage.removeItem(k));
                        if (orig.user_id) localStorage.setItem('user_id', String(orig.user_id));
                        if (orig.student_id) localStorage.setItem('student_id', String(orig.student_id));
                        if (orig.preceptor_id) localStorage.setItem('preceptor_id', String(orig.preceptor_id));
                        if (orig.admin_id) localStorage.setItem('admin_id', String(orig.admin_id));
                        localStorage.removeItem('impersonation_session');
                        localStorage.removeItem('admin_token');
                        if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('🟨 [IMPERSONATION] 401 during impersonation — admin session restored');
                        window.location.href = session.returnPath || '/AdminDashboard/manage-users';
                        return Promise.reject(new Error('Session expired'));
                    }
                }
            } catch {
                // fall through to full logout
            }

            localStorage.clear();
            window.location.href = '/';
            return Promise.reject(new Error('Session expired'));
        }

        return Promise.reject(error);
    }
);

export default axiosInstance;